/**
 * Pure TEA update for the project search palette.
 *
 * Shell: `ProjectSearchDialog.vue` (`useTea` + debounce/search/jump Cmds).
 */
import { assertNever } from "../lib/assertNever";
import type { SearchHit } from "../editor/projectSearch";
import { atIndexMaybe, isNothing } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type SearchModel = {
  readonly query: string;
  readonly hits: SearchHit[];
  readonly busy: boolean;
  readonly activeIndex: number;
};

export const initialSearchModel: SearchModel = {
  query: "",
  hits: [],
  busy: false,
  activeIndex: 0,
};

export type SearchMsg =
  | { readonly type: "Opened" }
  | { readonly type: "Closed" }
  | { readonly type: "QueryChanged"; readonly query: string }
  | { readonly type: "DebounceElapsed"; readonly query: string }
  | {
      readonly type: "SearchFinished";
      readonly query: string;
      readonly hits: SearchHit[];
    }
  | { readonly type: "MoveActive"; readonly delta: number }
  | { readonly type: "SetActiveIndex"; readonly index: number }
  | { readonly type: "ActivateActive" }
  | { readonly type: "ActivateHit"; readonly hit: SearchHit };

export type SearchCmd =
  | { readonly kind: "focusInput" }
  | { readonly kind: "clearDebounce" }
  | { readonly kind: "scheduleSearch"; readonly query: string }
  | { readonly kind: "runSearch"; readonly query: string }
  | { readonly kind: "scrollActiveIntoView"; readonly index: number }
  | { readonly kind: "jumpToHit"; readonly hit: SearchHit }
  | { readonly kind: "close" };

export function updateProjectSearch(
  model: SearchModel,
  msg: SearchMsg,
): readonly [SearchModel, readonly SearchCmd[]] {
  switch (msg.type) {
    case "Opened":
      return [initialSearchModel, [{ kind: "focusInput" }]];
    case "Closed":
      return [initialSearchModel, [{ kind: "clearDebounce" }]];
    case "QueryChanged": {
      const needle = msg.query.trim();
      if (needle.length === 0) {
        return [
          { query: msg.query, hits: [], busy: false, activeIndex: 0 },
          [{ kind: "clearDebounce" }],
        ];
      }
      // Shell's scheduleSearch clears any prior timer, then arms a new one.
      return [
        { ...model, query: msg.query, busy: true },
        [{ kind: "scheduleSearch", query: msg.query }],
      ];
    }
    case "DebounceElapsed": {
      if (msg.query !== model.query) return [model, []];
      const needle = msg.query.trim();
      if (needle.length === 0) {
        return [{ ...model, hits: [], busy: false, activeIndex: 0 }, []];
      }
      return [model, [{ kind: "runSearch", query: msg.query }]];
    }
    case "SearchFinished": {
      if (msg.query !== model.query) return [model, []];
      return [
        {
          ...model,
          hits: msg.hits,
          busy: false,
          activeIndex: 0,
        },
        [],
      ];
    }
    case "MoveActive": {
      const len = model.hits.length;
      if (len === 0) return [model, []];
      const activeIndex = (model.activeIndex + msg.delta + len) % len;
      return [
        { ...model, activeIndex },
        [{ kind: "scrollActiveIntoView", index: activeIndex }],
      ];
    }
    case "SetActiveIndex": {
      if (
        msg.index < 0 ||
        msg.index >= model.hits.length ||
        msg.index === model.activeIndex
      ) {
        return [model, []];
      }
      return [{ ...model, activeIndex: msg.index }, []];
    }
    case "ActivateActive": {
      const hit = atIndexMaybe(model.hits, model.activeIndex);
      if (isNothing(hit)) return [model, []];
      return [
        model,
        [{ kind: "jumpToHit", hit: hit.value }, { kind: "close" }],
      ];
    }
    case "ActivateHit":
      return [model, [{ kind: "jumpToHit", hit: msg.hit }, { kind: "close" }]];
    default:
      return assertNever(msg);
  }
}

export const updateProjectSearchFn: UpdateFn<
  SearchModel,
  SearchMsg,
  SearchCmd
> = updateProjectSearch;
